import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined"; // suspend
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined"; // unsuspend
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined"; // restore
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"; // delete
import { Avatar, IconButton, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { hashtagDetail } from "assets/data/hashtags";
import { useState } from "react";
import {
    HashtagCreatorObj,
    HashtagCreatorRow,
    HashtagDetailedObj
} from "utils/types/general.types";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { DemoUserObject } from "assets/data/usersData";

export const convertCreatorObj2Row = (
    arr: HashtagCreatorObj[]
): HashtagCreatorRow[] => {
    return arr.map((i) => {
        return {
            id: i.id,
            userId: i.user.id,
            name: i.user.name,
            username: i.user.username,
            url: i.user.url,
            createdAt: i.createdAt,
            country: i.country,
            noPosts: i.noPosts
        };
    });
};

const HashtagTable = () => {
    // hashtag details table
    const [hashtag, setHashtag] = useState(hashtagDetail);

    const handleSuspend = (state: boolean) => {
        setHashtag({ ...hashtag, suspended: state });
    };
    const handleDelete = (state: boolean) => {
        setHashtag({ ...hashtag, deleted: state });
    };

    const hashtagDetailsColumns: GridColDef[] = [
        { field: "name", headerName: "Name", flex: 2, minWidth: 200 },
        { field: "count", headerName: "Count", flex: 1, minWidth: 120 },
        { field: "noUsers", headerName: "No. Users", flex: 1, minWidth: 140 },
        {
            field: "noPostsInc",
            headerName: "No. Posts/Incidents",
            flex: 1,
            minWidth: 200
        },
        {
            field: "id",
            headerName: "Actions",
            renderCell: () => (
                <div className="flex justify-center items-center gap-3 w-full">
                    {hashtag.suspended ? (
                        // hashtag is suspended, make unsuspend option visible
                        <div
                            onClick={() => handleSuspend(false)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="unsuspend">
                                <IconButton>
                                    <PlayCircleFilledWhiteOutlinedIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        // hashtag is not suspended, make unsuspend option visible
                        <div
                            onClick={() => handleSuspend(true)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="suspend">
                                <IconButton>
                                    <PauseCircleOutlineOutlinedIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}

                    {hashtag.deleted ? (
                        // hashtag is deleted, make restore option visible
                        <div
                            onClick={() => handleDelete(false)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="restore">
                                <IconButton>
                                    <RestoreFromTrashOutlinedIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        // hashtag is not deleted, make delete option visible
                        <div
                            onClick={() => handleDelete(true)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="delete">
                                <IconButton>
                                    <DeleteForeverIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}
                </div>
            ),
            flex: 2,
            minWidth: 150,
            align: "center",
            headerAlign: "center"
        }
    ];

    // creators table
    const hashtagCreatorsColumns: GridColDef[] = [
        { field: "name", headerName: "Author Name", flex: 2, minWidth: 200 },
        { field: "username", headerName: "User Name", flex: 2, minWidth: 200 },
        {
            field: "country",
            headerName: "Country",
            flex: 1,
            minWidth: 130,
            renderCell: (params) => params.value.label
        },
        {
            field: "url",
            headerName: "Image",
            renderCell: (params) => (
                <div style={{ paddingTop: "5px" }}>
                    <Avatar src={params.value} />
                </div>
            ),
            flex: 1,
            minWidth: 120
        },
        {
            field: "createdAt",
            headerName: "Created At",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <span>{dayjs(params.value).format("YYYY-MM-DD HH:mm A")}</span>
            )
        }
    ];

    const hashtagTopActiveColumns: GridColDef[] = [
        { field: "name", headerName: "Full Name", flex: 2, minWidth: 200 },
        { field: "username", headerName: "User Name", flex: 2, minWidth: 200 },
        {
            field: "url",
            headerName: "Image",
            renderCell: (params) => (
                <div style={{ paddingTop: "5px" }}>
                    <Avatar src={params.value} />
                </div>
            ),
            flex: 1,
            minWidth: 120
        },
        {
            field: "id",
            headerName: "Actions",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <Link to={`/users/view/${params.value}`}>
                    <VisibilityRoundedIcon
                        className="dark:text-white"
                        fontSize="small"
                    />
                </Link>
            )
        }
    ];

    return (
        <>
            {/* hashtag details table */}
            <div className="w-full">
                <h1 className="dark:text-white font-semibold text-lg mb-2">
                    Hashtag Numbers:
                </h1>
                <div style={{ height: 165, width: "100%" }}>
                    <DataGrid
                        rows={[hashtag]}
                        columns={hashtagDetailsColumns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    page: 0,
                                    pageSize: 1
                                }
                            }
                        }}
                        // pageSizeOptions={[15, 30, 50, 100]}
                        getRowId={(row: HashtagDetailedObj) => row.id}
                        getRowClassName={(params) =>
                            `${params.row.suspended && "row-suspended"} ${params.row.deleted && "row-deleted"}`
                        }
                        className="w-full"
                    />
                </div>
            </div>
            {/* creators table */}
            <div className="w-full flex flex-col">
                <h1 className="dark:text-white font-semibold text-lg mb-2">
                    Hashtag Creators:
                </h1>
                <DataGrid
                    rows={convertCreatorObj2Row(hashtag.creators)}
                    columns={hashtagCreatorsColumns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 15
                            }
                        }
                    }}
                    pageSizeOptions={[15, 30, 50, 100]}
                    getRowId={(row: HashtagCreatorRow) => row.id}
                    className="w-full"
                />
            </div>

            {/* top active users details */}
            <div className="w-full flex flex-col">
                <h1 className="dark:text-white font-semibold text-lg mb-2">
                    Hashtag Top Active users:
                </h1>
                <DataGrid
                    rows={hashtag.TopActiveUsers}
                    columns={hashtagTopActiveColumns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 15
                            }
                        }
                    }}
                    pageSizeOptions={[15, 30, 50, 100]}
                    getRowId={(row: DemoUserObject) => row.id}
                    className="w-full"
                />
            </div>
        </>
    );
};

export default HashtagTable;
